/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

// Components
import { Button } from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import { DatePicker } from '@/components/Form/DatePicker/DatePicker';
import { Combobox } from '@/components/Combobox/Combobox';

// Hooks
import { useUpdateMeeting } from '../hooks/update-meeting.hook';
import { useBeneficiaries } from '../../Beneficiaries/hooks/beneficiaries.hook';

// Types
import { Meeting, MeetingStatus } from '@/types/meeting';

/* -------------------------------------------------------------------------- */
/*                              Validation Schema                             */
/* -------------------------------------------------------------------------- */
const meetingSchema = z.object({
  beneficiary: z.string().min(1, 'validation.beneficiary_required'),
  meetingDate: z.date().refine((date) => date > new Date(), {
    message: 'validation.meeting_date_in_futur',
  }),
  startAt: z.date(),
});

type MeetingValues = z.infer<typeof meetingSchema>;

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
type UpdateMeetingModalProps = {
  meeting: Meeting;
};
const UpdateMeetingModal = ({ meeting }: UpdateMeetingModalProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data, isFetching } = useBeneficiaries({
    params: { search: searchQuery, pageSize: 20 },
  });

  const beneficiaries =
    data?.pages
      .flatMap((item) => item.results)
      .map((beneficiary) => ({
        label: beneficiary.first_name + ' ' + beneficiary.last_name,
        value: beneficiary.id,
      })) ?? [];

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<MeetingValues>({
    resolver: zodResolver(meetingSchema),
    defaultValues: {
      meetingDate: (() => {
        const initialStartAtHours = parseInt(
          meeting.start_at.split(':')[0],
          10
        );
        const initialStartAtMinutes = parseInt(
          meeting.start_at.split(':')[1],
          10
        );
        const meetingDate = new Date(meeting.meeting_date);
        meetingDate.setHours(initialStartAtHours, initialStartAtMinutes, 0);
        return meetingDate;
      })(),
      startAt: (() => {
        const initialStartAtHours = parseInt(
          meeting.start_at.split(':')[0],
          10
        );
        const initialStartAtMinutes = parseInt(
          meeting.start_at.split(':')[1],
          10
        );
        const startDate = new Date(meeting.meeting_date);
        startDate.setHours(initialStartAtHours, initialStartAtMinutes, 0);
        return startDate;
      })(),
      beneficiary: meeting.beneficiary.id,
    },
  });

  const modal = useModal();

  const { mutate: updateMeeting, isPending } = useUpdateMeeting({
    onSuccess: () => {
      modal.remove();
    },
  });

  const handleClose = () => {
    modal.remove();
  };

  const onSubmit = (data: MeetingValues) => {
    const year = data.meetingDate.getFullYear();
    const month = String(data.meetingDate.getMonth() + 1).padStart(2, '0');
    const day = String(data.meetingDate.getDate()).padStart(2, '0');

    const hour = String(data.meetingDate.getHours()).padStart(2, '0');
    const minute = String(data.meetingDate.getMinutes()).padStart(2, '0');

    const formattedMeetingDate = `${year}-${month}-${day}`;

    const formattedStartAt = `${hour}:${minute}`;

    updateMeeting({
      id: meeting.id,
      data: {
        meetingDate: formattedMeetingDate,
        startAt: formattedStartAt,
        status:
          meeting.status === MeetingStatus.CANCELED
            ? MeetingStatus.COMING
            : undefined,
      },
    });
  };

  return (
    <Modal
      visible={modal.visible}
      onCancel={handleClose}
      title={t('pages.meetings.create_a_meeting')}
    >
      <div>{t('pages.meetings.pick_beneficiary_and_date')}</div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Combobox
              label={t('pages.meetings.select_beneficiary')}
              placeholder={t('pages.meetings.select_beneficiary')}
              items={beneficiaries}
              selectedItem={beneficiaries.find(
                (item) => item.value === watch('beneficiary')
              )}
              onSelect={(item: { value: string }) => {
                setValue('beneficiary', item.value);
              }}
              triggerClassName="mt-2"
              onSeachChange={setSearchQuery}
              isLoading={isFetching}
              error={errors.beneficiary}
            />
          </div>

          <div className="grid gap-2">
            <DatePicker
              date={watch('meetingDate')}
              label={t('pages.meetings.meeting_date')}
              setDate={(selectedDate) => {
                if (selectedDate) {
                  const updatedStartAt = new Date(selectedDate);
                  updatedStartAt.setHours(watch('startAt').getHours());
                  updatedStartAt.setMinutes(watch('startAt').getMinutes());

                  setValue('meetingDate', selectedDate);
                  setValue('startAt', updatedStartAt);
                }
              }}
              fromDate={new Date()}
              showTime={true}
              maxDate={
                new Date(new Date().setFullYear(new Date().getFullYear() + 1))
              }
              className="bg-gray-200 mt-2"
              error={errors.meetingDate}
            />
          </div>
        </div>

        <Button type="submit" className="w-full" isLoading={isPending}>
          {t('pages.meetings.edit_meeting')}
        </Button>
      </form>
    </Modal>
  );
};

export default NiceModal.create(UpdateMeetingModal);
